import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import SearchBar from "../components/SearchBar";
import { FaClock } from "react-icons/fa";
import "../styles/ModernPages.css";
import usePageMeta from "../hooks/usePageMeta";
import { pageTitle, breadcrumbSchema } from "../lib/seo";

function VisaNew() {
  usePageMeta({
    title: pageTitle("Tourist Visa Services for Indians"),
    description: "Tourist visa assistance for Indian passport holders — Dubai, Singapore, Japan, Vietnam, UK, USA, Schengen countries and more. Check documents required, processing time and fees.",
    jsonLd: breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Visa Services", path: "/visa" }]),
  });
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("country");

  useEffect(() => {
    fetchVisas();
  }, []);

  async function fetchVisas() {
    try {
      const { data } = await supabase
        .from("visas")
        .select("*")
        .order("country", { ascending: true });
      setVisas(data || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }

  const filteredVisas = useMemo(() => {
    let result = [...visas];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((v) => v.country?.toLowerCase().includes(query));
    }

    if (sortBy === "priceLow") {
      result.sort((a, b) => a.price - b.price);
    } else if (sortBy === "priceHigh") {
      result.sort((a, b) => b.price - a.price);
    }

    return result;
  }, [visas, searchQuery, sortBy]);

  function formatPrice(price) {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  }

  return (
    <div className="visa-new-page">
      <div className="page-hero">
        <h1>Visa Services</h1>
        <p>Fast, reliable visa processing for your international travel needs</p>
      </div>

      <div className="search-section">
        <div className="search-container">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by country..."
          />
        </div>
      </div>

      <div className="content-container">
        <div className="filters-bar">
          <select
            className="filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="country">Sort by Country</option>
            <option value="priceLow">Price: Low to High</option>
            <option value="priceHigh">Price: High to Low</option>
          </select>
        </div>

        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        )}

        {!loading && filteredVisas.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">🛂</div>
            <h3>No visa services found</h3>
            <p>Try a different search</p>
          </div>
        )}

        {!loading && filteredVisas.length > 0 && (
          <>
            <div className="cards-grid">
              {filteredVisas.map((visa) => (
                <Link
                  key={visa.id}
                  className="modern-card"
                  to={`/visa/${visa.id}/`}
                >
                  <div className="card-image">
                    <img
                      src={visa.image || "https://images.pexels.com/photos/7010170/pexels-photo-7010170.jpeg?auto=compress&w=600"}
                      alt={visa.country}
                      loading="lazy"
                    />
                    <div className="card-badge">{visa.visa_type || "Tourist"}</div>
                  </div>

                  <div className="card-content">
                    <h3 className="card-title">{visa.country} Visa</h3>

                    <div className="card-meta">
                      <div className="meta-item">
                        <FaClock />
                        <span>{visa.processing_time || "7-10 days"}</span>
                      </div>
                    </div>

                    <div className="card-footer">
                      <div className="card-price">
                        <div className="price-amount">{formatPrice(visa.price)}</div>
                        <div className="price-label">visa fee</div>
                      </div>
                      <span className="card-cta">View Details</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div style={{ textAlign: "center", marginTop: 40, color: "#64748b" }}>
              Showing {filteredVisas.length} visa service{filteredVisas.length !== 1 ? "s" : ""}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default VisaNew;